import { cn } from '@rocco/utils/cn';

import type { LocationDataType } from '@modules/professional/type/contact-page-types';
import {
    buildLocationRevealExtraData,
    getFullAddress,
} from '@modules/professional/util/contact-page-helper';
import { useClickToReveal } from '~/modules/root/hook/use-click-to-reveal';

interface ProfessionalLocationCardProps {
    location: LocationDataType;
    /** The page's professional, credited with the address and phone reveals. */
    professionalId: number;
    title: string;
    isHeadOffice: boolean;
    /** Offered once the address is revealed, so the reveal is always logged first. */
    onViewOnMap?: () => void;
    className?: string;
}

/**
 * One location on the Contact tab: name, place, the address and phone each
 * behind a reveal, and Click & Collect. The desktop map overlay, the mobile
 * list and the card under the mobile map all draw this, so a branch reads the
 * same wherever the reader finds it.
 */
export const ProfessionalLocationCard = ({
    location,
    professionalId,
    title,
    isHeadOffice,
    onViewOnMap,
    className,
}: ProfessionalLocationCardProps) => {
    // The `LocationReveal` the legacy contact page sends, keyed the same way
    // (by the branch's own profile, one sub-key per location) so a reveal
    // there or via `?expandAddress` carries over here for the session.
    const { revealed: showAddress, onRevealClick: revealAddress } =
        useClickToReveal({
            revealType: 'Location',
            revealKey: `professional-${location.professionalID ?? professionalId}`,
            revealSubKey: String(location.ID),
            professionalId,
            extraData: buildLocationRevealExtraData(location),
        });
    // The `PhoneReveal` lead the other phone reveals send, remembered per
    // location for the session. Credited to the page's professional, since a
    // branch can belong to a linked profile.
    const { revealed: showPhone, onRevealClick: revealPhone } =
        useClickToReveal({
            revealType: 'Phone',
            revealKey: `professional-${professionalId}-${location.ID}`,
            professionalId,
        });
    const place = [location.Suburb, location.City].filter(Boolean).join(', ');
    const fullAddress = getFullAddress(location);

    return (
        <div
            className={cn(
                'flex flex-col gap-[1.25rem] rounded-tile-card bg-[#fafafa] p-[1.5rem] text-[#141414] shadow-[0px_4px_14px_0px_rgba(0,0,0,0.1)]',
                className,
            )}
        >
            <div>
                <p className="m-0 truncate font-sans leading-[1.6] text-[#141414] max-md:text-lg md:text-[1.375rem] md:tracking-[-0.041rem]">
                    {title}
                </p>
                {place ? (
                    <p className="m-0 truncate font-sans leading-[1.6] text-[#aeaeae] max-md:text-sm md:text-base md:tracking-[-0.03rem]">
                        {isHeadOffice ? `Head Office: ${place}` : place}
                    </p>
                ) : null}
            </div>
            <div className="h-px w-full bg-black/10" />
            <div className="flex flex-col gap-1 text-sm text-[#141414]">
                {fullAddress ? (
                    showAddress ? (
                        <p className="m-0 font-sans leading-[1.43]">
                            {fullAddress}
                        </p>
                    ) : (
                        <button
                            type="button"
                            onClick={revealAddress}
                            data-button="professional-contact-accordion-address-reveal"
                            className="w-fit font-sans leading-[1.43] underline"
                        >
                            View address
                        </button>
                    )
                ) : null}
                {location.Phone ? (
                    showPhone ? (
                        <a
                            href={`tel:${location.Phone}`}
                            className="font-sans leading-[1.43] underline"
                        >
                            {location.Phone}
                        </a>
                    ) : (
                        <button
                            type="button"
                            onClick={revealPhone}
                            className="w-fit font-sans leading-[1.43] underline"
                        >
                            Show Phone number
                        </button>
                    )
                ) : null}
            </div>
            {location.AllowsPickup ? (
                <div className="flex items-center gap-2 text-sm text-[#141414]">
                    <svg
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        className="size-3 shrink-0"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                    >
                        <path d="M4 12l5 5L20 6" />
                    </svg>
                    Click &amp; Collect available
                </div>
            ) : null}
            {showAddress && onViewOnMap ? (
                <button
                    type="button"
                    onClick={onViewOnMap}
                    className="w-fit font-sans text-sm leading-[1.43] underline"
                >
                    View on map
                </button>
            ) : null}
        </div>
    );
};
